import { useCallback, useState } from 'react';
import {
  asChartRuleError,
  CHART_RESULT_TEMPLATE_MAX_LENGTH,
  fetchCustomChartData,
  useResolveChartRule,
  useSignChartRule,
  type ChartRule,
  type ChartRuleColumn,
  type ChartRuleResolveError,
  type CustomChartType,
} from '@/entities/dashboards';
import type { PresetChartFormValues, ResolvedPresetChart } from './dashboardPresetForm';
import { chartBindingDataParams } from './useCustomChartBindings';

/** Итог истолкования описания диаграммы. */
export type ChartResolutionOutcome = {
  /** Диаграмма с закреплённым правилом; при отказе — в исходном виде */
  chart: PresetChartFormValues;
  error: ChartRuleResolveError | null;
};

/**
 * Текст результата, набранный пользователем, и значения выборки, которыми он набран.
 *
 * Текст уходит истолкованию образцом оформления, а значения выборки говорят, есть ли что в нём
 * переписывать: пока их состав тот же, текст остаётся пользовательским целиком.
 */
export type ChartResultTextSample = {
  /** Текст, показанный в поле свойств, — в том числе ещё не сохранённый */
  text: string;
  /** Значения выборки, известные диаграмме до истолкования */
  columns: ChartRuleColumn[];
};

/** Отказ, показываемый вместо сообщения бэкенда, когда причина не пришла (сеть, сбой сервера). */
const UNKNOWN_ERROR: ChartRuleResolveError = {
  code: 'description_not_understood',
  message: '',
};

/**
 * Текст результата остаётся набранным пользователем: состав значений выборки прежний, и
 * переписывать в тексте нечего — подстановки заданы именами значений, а порядок их не важен.
 *
 * Перечень до истолкования неизвестен (правило в этот заход не исполнялось) — текст тоже
 * остаётся пользовательским: недостающее значение он допишет сам, а набранное оформление
 * вернуть будет неоткуда.
 */
const isResultTextKept = (before: ChartRuleColumn[], after: ChartRuleColumn[]) => {
  if (before.length === 0) return true;
  const names = new Set(before.map((column) => column.name));
  return names.size === after.length && after.every((column) => names.has(column.name));
};

/**
 * Истолкование описания диаграммы и заверение правленого вручную запроса.
 *
 * К модели идёт только само описание, и только когда его правили: правка названия, типа или
 * размера правило не трогает, а повторное обращение к модели дало бы другое правило на том же
 * тексте. Смена типа правило тоже не перестраивает, но требует проверки — правило может не
 * давать данных нужной новому типу формы, и об этом пользователь должен узнать сразу по выбору
 * типа, а не пустым блоком на дашборде.
 *
 * Запрос, правленный вручную, к модели не ходит вовсе: он заверяется как есть, а описание при
 * этом остаётся подписью к диаграмме и с запросом больше не сверяется.
 */
export const useChartRuleResolution = () => {
  const { mutateAsync: resolveRule } = useResolveChartRule();
  const { mutateAsync: signRule } = useSignChartRule();
  // Свой признак, а не `isPending` мутации: проверка правила под новый тип идёт наполнением
  // диаграммы, а его состояние мутация истолкования не показывает.
  const [isResolving, setIsResolving] = useState(false);

  /**
   * Истолкование описания заводимой диаграммы: тип в запрос не идёт, а название, тип и размер
   * приходят вместе с правилом — заполнять их до истолкования значило бы просить пользователя
   * угадать, что получится.
   */
  const resolveNewChart = useCallback(
    async (chart: PresetChartFormValues, description: string): Promise<ChartResolutionOutcome> => {
      setIsResolving(true);
      try {
        const resolved = await resolveRule({ description: description.trim() });
        return {
          chart: {
            ...chart,
            title: resolved.title,
            chartType: resolved.chartType,
            size: resolved.size,
            description,
            rule: resolved.rule,
            signature: resolved.signature,
            summary: resolved.summary,
            resolvedDescription: description,
            resolvedChartType: resolved.chartType,
            columns: resolved.columns,
            bindings: resolved.bindings,
            // Начальное значение разреза приходит вместе с ним: до выбора пользователя диаграмма
            // показана именно им.
            sliceValue: resolved.bindings.find((binding) => binding.kind === 'slice')?.initial ?? null,
          },
          error: null,
        };
      } catch (error) {
        return {
          chart: { ...chart, description },
          error: asChartRuleError(error) ?? UNKNOWN_ERROR,
        };
      } finally {
        setIsResolving(false);
      }
    },
    [resolveRule],
  );

  /**
   * Истолкование правленого описания сохранённой диаграммы: тип у неё уже выбран и в запрос идёт,
   * а название и размер остаются прежними — их задаёт пользователь, а не описание. Отказ прежнего
   * правила не отменяет: диаграмма показана тем, что за ней закреплено, пока описание не понято.
   *
   * Набранный текст результата истолкование не переписывает: пока состав значений выборки тот же,
   * из нового правила берётся один запрос, а изменившийся состав меняет текст, но не его вид —
   * прежний текст уходит модели образцом оформления.
   */
  const resolveDescription = useCallback(
    async (
      chart: PresetChartFormValues,
      description: string,
      resultText: ChartResultTextSample,
    ): Promise<ChartResolutionOutcome> => {
      setIsResolving(true);
      try {
        const hasResultText = resultText.text.length > 0;
        // Образец длиннее предела правила отказом кончил бы само истолкование, поэтому такой
        // текст идёт без образца: правило нужнее, чем перенос оформления.
        const sample =
          hasResultText && resultText.text.length <= CHART_RESULT_TEMPLATE_MAX_LENGTH ? resultText.text : null;
        const { rule, signature, summary, columns, bindings } = await resolveRule({
          chartType: chart.chartType,
          description: description.trim(),
          ...(sample ? { template: sample } : {}),
        });
        const resolvedRule =
          hasResultText && isResultTextKept(resultText.columns, columns)
            ? { ...rule, template: resultText.text }
            : rule;
        return {
          chart: {
            ...chart,
            description,
            rule: resolvedRule,
            signature,
            summary,
            resolvedDescription: description,
            resolvedChartType: chart.chartType,
            columns,
            bindings,
            // Разрез мог смениться вместе с правилом: прежнее значение к новому разрезу не
            // относится, поэтому берётся начальное значение нового.
            sliceValue: bindings.find((binding) => binding.kind === 'slice')?.initial ?? null,
          },
          error: null,
        };
      } catch (error) {
        return { chart: { ...chart, description }, error: asChartRuleError(error) ?? UNKNOWN_ERROR };
      } finally {
        setIsResolving(false);
      }
    },
    [resolveRule],
  );

  /**
   * Проверка правила под выбранный тип диаграммы: правило исполняется наполнением, и подходящие
   * типы приходят тем же отказом, что и при истолковании. Само правило тип не перестраивает.
   */
  const verifyChartType = useCallback(
    async (chart: ResolvedPresetChart, chartType: CustomChartType): Promise<ChartResolutionOutcome> => {
      setIsResolving(true);
      try {
        // Проверка идёт тем же наполнением, что и показ диаграммы, поэтому и места подстановки
        // ей нужны те же: без них запрос правила не исполнится, а заверение не сойдётся.
        const { columns } = await fetchCustomChartData({
          chartType,
          rule: chart.rule,
          signature: chart.signature || null,
          ...chartBindingDataParams(chart),
        });
        return { chart: { ...chart, chartType, resolvedChartType: chartType, columns }, error: null };
      } catch (error) {
        return { chart: { ...chart, chartType }, error: asChartRuleError(error) ?? UNKNOWN_ERROR };
      } finally {
        setIsResolving(false);
      }
    },
    [],
  );

  /**
   * Заверяет правленый вручную запрос: бэкенд проверяет его и пробно исполняет, и только
   * прошедший проверку заменяет прежнее правило диаграммы.
   *
   * Места подстановки остаются прежними: заверение их не пересобирает — в правленом вручную
   * запросе их называет сам пользователь, и переписывать заданный им разрез нечем.
   */
  const signChart = useCallback(
    async (chart: PresetChartFormValues, rule: ChartRule): Promise<ChartResolutionOutcome> => {
      setIsResolving(true);
      try {
        const { rule: signedRule, signature, summary, columns } = await signRule({ chartType: chart.chartType, rule });
        return {
          chart: {
            ...chart,
            rule: signedRule,
            signature,
            summary,
            columns,
            // Описание правленому запросу больше не хозяин: истолковывать его заново нужно
            // только тогда, когда пользователь сам его изменит.
            resolvedDescription: chart.description,
            resolvedChartType: chart.chartType,
          },
          error: null,
        };
      } catch (error) {
        return { chart, error: asChartRuleError(error) ?? UNKNOWN_ERROR };
      } finally {
        setIsResolving(false);
      }
    },
    [signRule],
  );

  return { resolveNewChart, resolveDescription, verifyChartType, signChart, isResolving };
};
